/**
 * FE mirror of the backend source-registry read contract.
 *
 * Source of truth: `backend/app/modules/agentic/structured/api/router.py`
 * (`SourceRegistryOut`). Drives the tick state in the "Connect to Data Room"
 * dialog and the source sections under DEAL ROOM in the sidebar. Hand-rolled
 * boundary validation follows the repo's citationArtifact convention.
 */

export type SourceStatus = "demo_snapshot" | "connected" | "disconnected";

export interface RegisteredSource {
	public_id: string;
	source_system: string;
	display_name: string;
	status: SourceStatus;
	last_synced_at_ms: number | null;
	item_count: number;
	snapshot_sha256: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

const STATUSES: readonly SourceStatus[] = [
	"demo_snapshot",
	"connected",
	"disconnected",
];

/** Validate one row at the boundary; returns `null` on mismatch (row dropped). */
export function parseRegisteredSource(input: unknown): RegisteredSource | null {
	if (!isRecord(input)) return null;
	if (typeof input.public_id !== "string") return null;
	if (typeof input.source_system !== "string") return null;
	if (typeof input.display_name !== "string") return null;
	const status = STATUSES.includes(input.status as SourceStatus)
		? (input.status as SourceStatus)
		: "disconnected";
	return {
		public_id: input.public_id,
		source_system: input.source_system,
		display_name: input.display_name,
		status,
		last_synced_at_ms:
			typeof input.last_synced_at_ms === "number"
				? input.last_synced_at_ms
				: null,
		item_count: typeof input.item_count === "number" ? input.item_count : 0,
		snapshot_sha256:
			typeof input.snapshot_sha256 === "string" ? input.snapshot_sha256 : null,
	};
}

/** Parse a list payload, dropping malformed rows rather than failing wholesale. */
export function parseRegisteredSources(input: unknown): RegisteredSource[] {
	if (!Array.isArray(input)) return [];
	return input
		.map(parseRegisteredSource)
		.filter((row): row is RegisteredSource => row !== null);
}
